import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";

const ViewCuttingApplication = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) {
      fetchCuttingDetails();
    }
  }, [id]);

  const fetchCuttingDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/permissions/cutting/${id}`);
      if (response.status === 200 || response.status === 201) {
        // Handle different response structures
        if (response.data.data) {
          setData(response.data.data);
        } else if (response.data) {
          setData(response.data);
        } else {
          setError("Invalid response format");
        }
      }
    } catch (err) {
      console.error("Error fetching cutting details:", err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || "Failed to load application details";
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Parse tree details
  const parseTreeDetails = (data) => {
    const trees = [];
    try {
      const treeNames = data.tree_name ? (typeof data.tree_name === 'string' ? data.tree_name.split(',') : [data.tree_name]) : [];
      const counts = data.count ? (typeof data.count === 'string' ? data.count.split(',') : [data.count]) : [];
      const ages = data.agesapproved ? (typeof data.agesapproved === 'string' ? data.agesapproved.split(',') : [data.agesapproved]) : [];
      const names = data.namesapproved ? (typeof data.namesapproved === 'string' ? data.namesapproved.split(',') : [data.namesapproved]) : [];
      
      const maxLength = Math.max(treeNames.length, counts.length, ages.length, names.length);
      
      for (let i = 0; i < maxLength; i++) {
        if (treeNames[i] || counts[i]) {
          trees.push({
            treeNo: i + 1,
            treeName: treeNames[i] || names[i] || "N/A",
            noOfTrees: counts[i] || "1",
            age: ages[i] || "N/A",
          });
        }
      }
    } catch (error) {
      console.error("Error parsing tree details:", error);
      if (data.count || data.tree_name) {
        trees.push({
          treeNo: 1,
          treeName: data.tree_name || data.namesapproved || "N/A",
          noOfTrees: data.count || "1",
          age: data.agesapproved || "N/A",
        });
      }
    }
    return trees.length > 0 ? trees : [{ treeNo: 1, treeName: "N/A", noOfTrees: data.count || "1", age: "N/A" }];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading application details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="p-6">
        <div className="text-center">
          <p className="text-gray-600">No data found.</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const trees = parseTreeDetails(data);

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="bg-blue-600 text-white px-6 py-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="bg-white text-gray-800 px-3 py-1 rounded-md hover:bg-gray-100"
            >
              ← Back
            </button>
            <h2 className="text-2xl font-bold">Tree Cutting Application Form</h2>
          </div>
        </div>

        <div className="p-6">
          {/* Permission Details */}
          <div className="mb-6">
            <h3 className="text-xl font-semibold mb-4 border-b pb-2">Permission Details</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Permission ID</label>
                <p className="bg-gray-50 p-2 rounded-md">{data.permission_id || "-"}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <p className="bg-gray-50 p-2 rounded-md">
                  {data.first_name && data.last_name 
                    ? `${data.first_name} ${data.last_name}` 
                    : data.name || data.user_name || "-"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <p className="bg-gray-50 p-2 rounded-md">{data.email || "-"}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                <p className="bg-gray-50 p-2 rounded-md">{data.mobile || "-"}</p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <p className="bg-gray-50 p-2 rounded-md">{data.address || "-"}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Area</label>
                <p className="bg-gray-50 p-2 rounded-md">{data.area || "-"}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Division</label>
                <p className="bg-gray-50 p-2 rounded-md">{data.division || "-"}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Ward No</label>
                <p className="bg-gray-50 p-2 rounded-md">{data.ward_no || data.wardno || "-"}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
                <p className="bg-gray-50 p-2 rounded-md">
                  {data.purpose ? data.purpose.replace(/_/g, " ") : "-"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Permission Status</label>
                <p className="bg-yellow-50 p-2 rounded-md font-medium">
                  {data.permission_status || data.status || "-"}
                </p>
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <p className="bg-gray-50 p-2 rounded-md whitespace-pre-wrap">
                  {data.reason || "-"}
                </p>
              </div>
            </div>
          </div>

          {/* Requested Tree Details */}
          <div className="mb-6">
            <h3 className="text-xl font-semibold mb-4 border-b pb-2">Requested Tree Details</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tree No.</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tree Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">No. of Trees</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Age</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trees.map((tree) => (
                    <tr key={tree.treeNo}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{tree.treeNo}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{tree.treeName}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{tree.noOfTrees}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{tree.age}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Approval Tables */}
          <div className="mb-6">
            <h3 className="text-xl font-semibold mb-4 border-b pb-2">Approval Status</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Officer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Remark</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">Garden Inspector</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {data.statusbygardenassistant || "Raised"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {data.remarkbygardenassistant || "-"}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">Divisional Officer</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {data.statusbydivisionalofficer || "Raised"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {data.remarkbydivisionalofficer || "-"}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">Garden Superintendent</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {data.statusbygardensuperintendent || "Raised"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {data.remarkbygardensuperintendent || "-"}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">Additional Commissioner</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {data.statusbyadditional || "Raised"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {data.remarkbyadditional || "-"}
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">Commissioner</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {data.statusbycommissioner || data.statusbycommisioner || "Raised"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {data.remarkbycommissioner || "-"}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Documents */}
          <div className="mb-6">
            <h3 className="text-xl font-semibold mb-4 border-b pb-2">Documents</h3>
            <div className="grid grid-cols-2 gap-4">
              {data.panchanama && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Panchanama</label>
                  <a
                    href={data.panchanama}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-900 underline text-sm"
                  >
                    View Document
                  </a>
                </div>
              )}
              {data.consent_letter && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Consent Letter</label>
                  <a
                    href={data.consent_letter}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-900 underline text-sm"
                  >
                    View Document
                  </a>
                </div>
              )}
              {data.certificate7_12 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Certificate 7/12</label>
                  <a
                    href={data.certificate7_12}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-900 underline text-sm"
                  >
                    View Document
                  </a>
                </div>
              )}
              {data.form_c && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Form C</label>
                  <a
                    href={data.form_c}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-900 underline text-sm"
                  >
                    View Document
                  </a>
                </div>
              )}
              {data.form_d && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Form D</label>
                  <a
                    href={data.form_d}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-900 underline text-sm"
                  >
                    View Document
                  </a>
                </div>
              )}
              {data.tree_images && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tree Images</label>
                  <a
                    href={data.tree_images}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-900 underline text-sm"
                  >
                    View Images
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Payment Details */}
          {data.payment_status === "Payment Completed" && (
            <div className="mb-6">
              <h3 className="text-xl font-semibold mb-4 border-b pb-2">Payment Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Status</label>
                  <p className="bg-gray-50 p-2 rounded-md">{data.payment_status}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Merchant ID</label>
                  <p className="bg-gray-50 p-2 rounded-md">{data.payment_merchant_id || "-"}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Amount</label>
                  <p className="bg-gray-50 p-2 rounded-md">{data.payment_amount || "-"}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date</label>
                  <p className="bg-gray-50 p-2 rounded-md">{data.payment_date || "-"}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewCuttingApplication;

